import db from './db';
import { buildChannelSnapshot, buildEmojiSnapshot, buildProfileSnapshot, extractEntityTargets } from './entitySnapshotBuilders';

export { extractEntityTargets } from './entitySnapshotBuilders';

export const loadEntitySnapshotsForTargets = async (workspaceId, targets = {}) => {
  if (workspaceId == null) return {};

  const normalizedWorkspaceId = String(workspaceId);
  const { profile = [], channel = [], emoji = [] } = targets;
  const [profileRows, channelRows, workspaceEmojiRows, systemEmojiRows] = await Promise.all([
    profile.length ? db.profiles.bulkGet(profile.map((id) => [normalizedWorkspaceId, id])) : [],
    channel.length ? db.channels.bulkGet(channel.map((id) => [normalizedWorkspaceId, id])) : [],
    emoji.length ? db.emojis.bulkGet(emoji.map((id) => [normalizedWorkspaceId, id])) : [],
    emoji.length ? db.emojis.bulkGet(emoji.map((id) => ['system', id])) : [],
  ]);

  // Custom emojis from the user's other chats are usable here, so ids that miss
  // both lookups above resolve against the full custom set (it's small).
  let crossWorkspaceEmojiById = null;
  if (emoji.some((id, i) => !workspaceEmojiRows[i] && !systemEmojiRows[i])) {
    const customRows = await db.emojis.where('workspace_id').notEqual('system').toArray();
    crossWorkspaceEmojiById = new Map(customRows.map((row) => [row.id, row]));
  }

  const result = {};
  if (profile.length) {
    result.profile = Object.fromEntries(profile.map((id, i) => [id, profileRows[i] ? buildProfileSnapshot(profileRows[i]) : null]));
  }
  if (channel.length) {
    result.channel = Object.fromEntries(channel.map((id, i) => [id, channelRows[i] ? buildChannelSnapshot(channelRows[i]) : null]));
  }
  if (emoji.length) {
    result.emoji = Object.fromEntries(
      emoji.map((id, i) => {
        const row = workspaceEmojiRows[i] || systemEmojiRows[i] || crossWorkspaceEmojiById?.get(id);
        return [id, row ? buildEmojiSnapshot(row) : null];
      }),
    );
  }
  return result;
};

// Async loader for code paths that read a message straight out of Dexie and don't have
// the in-memory snapshots the feed enriches onto rendered rows. Mirrors the shape
// of `buildMessageEntitySnapshots` so consumers can hand the result to anything that
// already accepts entity snapshots. Returns null when the message has no entities so the
// caller can skip the substitution work entirely.
export const loadMessageEntitySnapshots = async (message) => {
  if (!message?.text || message.workspace_id == null) return null;

  const { profile, channel, emoji } = extractEntityTargets(message.text);
  if (!profile.length && !channel.length && !emoji.length) return null;

  return loadEntitySnapshotsForTargets(message.workspace_id, { profile, channel, emoji });
};

export const buildMessageEntitySnapshots = (html, snapshots) => {
  if (!html || !snapshots) return {};

  const { channel, profile, emoji } = extractEntityTargets(html);
  const result = {};

  if (channel.length > 0) {
    const channelSnaps = {};
    channel.forEach((id) => {
      channelSnaps[id] = snapshots.channel?.get(id) ?? null;
    });
    if (Object.keys(channelSnaps).length > 0) result.channel = channelSnaps;
  }

  if (profile.length > 0) {
    const profileSnaps = {};
    profile.forEach((id) => {
      profileSnaps[id] = snapshots.profile?.get(id) ?? null;
    });
    if (Object.keys(profileSnaps).length > 0) result.profile = profileSnaps;
  }

  if (emoji.length > 0) {
    const emojiSnaps = {};
    emoji.forEach((id) => {
      emojiSnaps[id] = snapshots.emoji?.get(id) ?? null;
    });
    if (Object.keys(emojiSnaps).length > 0) result.emoji = emojiSnaps;
  }

  return result;
};
